import { useState, useEffect } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as KaizIQText } from '@Assets/kaiziqText.svg';
import { ReactComponent as StartupIcon } from '@Assets/startup.svg';
import { ReactComponent as SecureIcon } from '@Assets/secure.svg';
import { ReactComponent as SmartIcon } from '@Assets/smart.svg';
import { GoogleLogin } from '@react-oauth/google';
import { GoogleOAuthProvider } from '@react-oauth/google';
import CircularProgress from '@mui/material/CircularProgress';
import DOMPurify from 'dompurify';
import SignupService from '@Helpers/URLHelpers/SignupService';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import UserPreferenceService from '@Helpers/URLHelpers/UserPreferenceService';
import { validateGoogleIdToken } from '@Helpers/googleAuth/validateGoogleIdToken';
import { validate, res } from 'react-email-validator';
import { MdPerson, MdEmail, MdWarning } from 'react-icons/md';
import { FcGoogle } from 'react-icons/fc';
import { BiShow, BiHide } from 'react-icons/bi';
import './SignupPage.scss';

export default function Signup({ setToken, setEntryPage }) {
    const [emailLoader, setEmailLoader] = useState(false);
    const [signupLoader, setSignupLoader] = useState(false);
    const [name, setName] = useState(null);
    const [email, setEmail] = useState(null);
    const [cursor, setCursor] = useState('auto');
    const [errorCreatingUser, setErrorCreatingUser] = useState(null);
    const [emailTaken, setEmailTaken] = useState(false);
    const [invalidName, setInvalidName] = useState(false);
    const [invalidEmail, setInvalidEmail] = useState(false);
    const [userCreated, setUserCreated] = useState(null);
    const [signupError, setSignupError] = useState(false);
    const [googleValidated, setGoogleValidated] = useState(false);
    const [googleErrors, setGoogleErrors] = useState([]);
    const [conflictType, setConflictType] = useState(null);
    const [existingPlatform, setExistingPlatform] = useState(null);
    const [isFading, setIsFading] = useState(false);
    const [preload, setPreload] = useState(true);
    const [showErrorView, setShowErrorView] = useState(false);

    const signupService = new SignupService();
    const navigate = useNavigate();
    const location = useLocation();
    const { setCurrentOrg } = useReduxOrg();
    const { setActiveUserJourney, setActiveUserPermission, setActiveUserPreference, setActiveUserAccessList, setActiveUser } = useReduxActiveUser();
    const userAuthService = new UserAuthService();
    const userPreferenceService = new UserPreferenceService();
    const className = 'signupPage';
    const GOOGLE_CLIENT_ID = '704888664600-l1g177ip7drg2060329ievengm13o6j0.apps.googleusercontent.com';

    useEffect(() => {
        const t = setTimeout(() => setPreload(false), 20);
        return () => clearTimeout(t);
    }, []);

    const triggerFadeAnd = (cb) => {
        setIsFading(true);
        setTimeout(() => {
            cb();
        }, 300);
    };

    const switchToErrorView = () => {
        setIsFading(true);
        setTimeout(() => {
            setShowErrorView(true);
            requestAnimationFrame(() => setIsFading(false));
        }, 300);
    };

    const tryAgain = (e) => {
        e?.preventDefault();
        setIsFading(true);
        setTimeout(() => {
            setShowErrorView(false);
            setErrorCreatingUser(null);
            setConflictType(null);
            setExistingPlatform(null);
            requestAnimationFrame(() => setIsFading(false));
        }, 300);
    };

    function backToLogin(e) {
        e?.preventDefault();
        triggerFadeAnd(() => setEntryPage('login'));
    }

    function emailValidCheck(email) {
        validate(email);
        return res;
    }

    function nameNotNullCheck(name) {
        return name !== '' && name !== null;
    }

    const emailTakenCheck = async (email) => {
        let user = await signupService.getUserByEmailForSignup(email);
        if (!!user && !!user?.body && !!JSON.parse(user?.body) && Object.keys(JSON.parse(user?.body)).length !== 0 && JSON.parse(user?.body)?.id > 0)
            return true;
        return false;
    }

    const emailChangeHandler = async e => {
        setEmailLoader(true);
        setEmailTaken(null);
        setConflictType(null);
        setExistingPlatform(null);
        setErrorCreatingUser(null);
        let sanitizedEmail = DOMPurify.sanitize(e.target.value);
        setEmail(sanitizedEmail);
        let emailValid = emailValidCheck(sanitizedEmail);
        setInvalidEmail(!emailValid);
        let emailTakenVar;
        if (emailValid) {
            emailTakenVar = await emailTakenCheck(sanitizedEmail);
            setEmailTaken(emailTakenVar);
        }
        setEmailLoader(false);
    }

    const nameChangeHandler = async e => {
        let sanitizedName = DOMPurify.sanitize(e.target.value);
        setName(sanitizedName);
        let nameValid = nameNotNullCheck(sanitizedName);
        setInvalidName(!nameValid);
    }

    const handleSignup = async (e, signupObject) => {
        setSignupLoader(true);
        e?.preventDefault();
        let nameVerification;
        let emailVerification;
        if(!!signupObject?.loginMethod) {
            nameVerification = signupObject?.loginMethod === 'social' ? signupObject?.fullName : name;
            emailVerification = signupObject?.loginMethod === 'social' ? signupObject?.email : email;
        } else {
            nameVerification = signupObject?.signupMethod === 'social' ? signupObject?.fullName : name;
            emailVerification = signupObject?.signupMethod === 'social' ? signupObject?.email : email;
        }
        let nameValid = nameNotNullCheck(nameVerification);
        let emailValid = emailValidCheck(emailVerification);

        setInvalidName(!nameValid);
        setInvalidEmail(!emailValid);

        if (nameValid && emailValid && signupObject?.loginMethod !== 'social') {
            try {
                const signup = await signupService.signupUser(signupObject);

                if (signup && signup.conflictType) {
                    setUserCreated(false);
                    setConflictType(signup.conflictType);
                    setExistingPlatform(signup.existingPlatform || null);
                    setErrorCreatingUser(signup.message || 'Account already exists.');
                    setSignupLoader(false);
                    switchToErrorView();
                    return;
                }

                if (signup?.body !== undefined) {
                    setUserCreated(true);
                    if (signup.token !== 'void') {
                        let activeUserPreferences, activeUserJourney, activeOrg;
                        setActiveUser(JSON.parse(signup.activeUser));
                        setActiveUserPermission(JSON.parse(signup.activeUserPermissions));
                        if(!!signup.activeUserPreferences){
                            activeUserPreferences = JSON.parse(signup.activeUserPreferences);
                        } else {
                            activeUserPreferences = {};
                            setActiveUserPreference({});
                        }
                        if(!!signup.activeUserJourney){
                            activeUserJourney = JSON.parse(signup.activeUserJourney);
                            setActiveUserJourney(activeUserJourney);
                        } else {
                            activeUserJourney = {};
                            setActiveUserJourney({});
                        }

                        let navHistory = []

                        let accessList = await userAuthService.getAccessListForUser(signup.activeUserId);
                        accessList = JSON.parse(accessList.body);
                        setActiveUserAccessList(accessList);
                        if(!!JSON.parse(activeUserPreferences.preferences)?.defaults?.orgId) {
                            activeOrg = accessList.orgs.find((org) => {
                                return ((org.id === JSON.parse(activeUserPreferences.preferences)?.defaults?.orgId) || (org.id === JSON.parse(JSON.parse(activeUserPreferences.preferences)?.defaults?.orgId)));
                            });
                        } else if (accessList?.orgs?.length > 0) {
                            activeOrg = accessList.orgs[0]
                            let newActiveUserPreferences = activeUserPreferences;
                            let newPreferences = JSON.parse(newActiveUserPreferences.preferences);
                            newPreferences.defaults = {};
                            newPreferences.defaults.orgId = activeOrg?.id;
                            newActiveUserPreferences.preferences = JSON.stringify(newPreferences);
                            setActiveUserPreference(newActiveUserPreferences);
                        } else {
                            activeOrg = {
                                id: 0,
                                title: 'N/A'
                            }
                        }
                        if(accessList?.orgs?.length > 0) {
                            activeOrg = accessList.orgs[0];
                            setCurrentOrg(activeOrg);
                        } else {
                            navigate('/onboarding');
                        }
                    } else {
                        setSignupError(true);
                    }
                    if(!!signup && signup.token !== 'void') {
                        setToken(signup);
                        setEntryPage('signupSuccess');
                        const from = location.state?.from?.pathname || '/onboarding';
                        navigate(from, { replace: true });
                    }
                } else {
                    setUserCreated(false);
                    setErrorCreatingUser(signup?.message || 'We couldn\'t complete your sign up. Please try again.');
                    switchToErrorView();
                }
            } catch (err) {
                setUserCreated(false);
                setErrorCreatingUser('We couldn\'t complete your sign up due to a server issue. Please try again.');
                switchToErrorView();
            }
        } else {
            setUserCreated(false);
            if (!nameValid) {
                setErrorCreatingUser('Full Name required');
            } else if (!emailValid) {
                setErrorCreatingUser('Invalid Email');
            }
            switchToErrorView();
        }

        setSignupLoader(false);
    }

    const handleGoogleSuccess = (credentialResponse) => {
        const rawCred = credentialResponse.credential || '';
        const { valid, errors, payload } = validateGoogleIdToken(rawCred, { expectedAud: GOOGLE_CLIENT_ID });
        setGoogleValidated(valid);
        setGoogleErrors(errors);
        if (!valid) {
            setErrorCreatingUser('Google Sign-In Failed');
            switchToErrorView();
            return;
        }
        const googleEmail = payload.email;
        const fullName = payload.name || (payload.given_name && payload.family_name ? payload.given_name + ' ' + payload.family_name : undefined);
        if (googleEmail) {
            setEmail(googleEmail);
            setInvalidEmail(false);
        }
        if (fullName) {
            setName(fullName);
            setInvalidName(false);
        }
        if (!googleEmail || !fullName) {
            setErrorCreatingUser('Google Sign-In Failed');
            switchToErrorView();
            return;
        }
        const signupObject = {
            email: googleEmail,
            fullName: fullName,
            signupMethod: 'social',
            socialPlatform: 'google',
            sub: payload.sub,
            picture: payload.picture,
            emailVerified: payload.email_verified
        };
        if (payload.email_verified) {
            handleSignup(null, signupObject);
        } else {
            setErrorCreatingUser('Google email not verified');
            switchToErrorView();
        }
    };

    const handleSignupClick = () => {
        let signupObject = {
            email,
            fullName: name,
            signupMethod: 'manual',
        };
        handleSignup(null, signupObject);
    };

    const handleGoogleFailure = () => {
        setErrorCreatingUser('Google Sign-In Failed');
        switchToErrorView();
    };

    const openTermsPage = () => {
        window.open('https://kaiziq.io/legal/termsAndConditions', '_blank', 'noopener,noreferrer');
    };

    return (
        <div className={className} style={{ cursor: cursor }}>
            {/* Background Elements */}
            <div className={className + '__background'}>
                <div className={className + '__background__gradient'}></div>
                <div className={className + '__background__pattern'}></div>
                <div className={className + '__background__shapes'}>
                    <div className={className + '__background__shape --shape1'}></div>
                    <div className={className + '__background__shape --shape2'}></div>
                    <div className={className + '__background__shape --shape3'}></div>
                </div>
            </div>

            {/* Main Container */}
            <div className={className + '__container'}>
                {/* Left Side - Branding */}
                <div className={className + '__branding'}>
                    <div className={className + '__branding__content'}>
                        <div className={className + '__branding__logo'}>
                            <KaizIQText className={className + '__branding__logoSvg'} />
                        </div>
                        <div className={className + '__branding__hero'}>
                            <h1 className={className + '__branding__title'}>
                                Join the Future of
                                <span className={className + '__branding__titleAccent'}> Productivity</span>
                            </h1>
                            <p className={className + '__branding__subtitle'}>
                                Intelligent task management and efficiency tracking designed for modern teams
                            </p>
                        </div>
                        <div className={className + '__branding__features'}>
                            <div className={className + '__branding__feature'}>
                                <StartupIcon className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Quick Setup</strong>
                                    <span>Start managing tasks and tracking performance in under two minutes</span>
                                </div>
                            </div>
                            <div className={className + '__branding__feature'}>
                                <SecureIcon className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Secure & Private</strong>
                                    <span>Protected with enterprise-grade security and data privacy</span>
                                </div>
                            </div>
                            <div className={className + '__branding__feature'}>
                                <SmartIcon className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Smart Insights</strong>
                                    <span>Gain meaningful performance insights and automate routine workflows</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Signup Form */}
                <div className={className + '__formSection'}>
                    <div className={className + '__formContainer' +
                        (preload ? ' ' + className + '__formContainer--preload' : '') +
                        (isFading ? ' ' + className + '__formContainer--fadeOut' : '')}>

                        {/* Header */}
                        <div className={className + '__header'}>
                            <h2 className={className + '__welcomeTitle'}>Create your account</h2>
                            <p className={className + '__welcomeSubtitle grey-text'}>
                                Start your productivity journey today
                            </p>
                        </div>

                        {!showErrorView && (
                            <div className={className + '__signupForm'}>
                                {/* Google Signup */}
                                <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={handleGoogleFailure}
                                        useOneTap={false}
                                        text='continue_with'
                                        logo_alignment='center'
                                        shape='circle'
                                        size='medium'
                                        outline='none'/>
                                </GoogleOAuthProvider>

                                {googleErrors.length > 0 && (
                                    <div className={className + '__error'}>
                                        Google auth issues: {googleErrors.join(', ')}
                                    </div>
                                )}

                                {/* Divider */}
                                <div className={className + '__divider'}>
                                    <span className={className + '__dividerLine'}></span>
                                    <span className={className + '__dividerText grey-text sm-text'}>OR</span>
                                    <span className={className + '__dividerLine'}></span>
                                </div>

                                {/* Manual Signup Form */}
                                <form onSubmit={(e) => e.preventDefault()} className={className + '__form'}>
                                    <div className={className + '__inputGroup'}>
                                        <label className={className + '__label grey-text sm-text'}>Full Name</label>
                                        <div className={className + '__inputWrapper flex-row-center'}>
                                            <MdPerson className={className + '__inputIcon'} />
                                            <input
                                                type='text'
                                                className={`${className}__input ${invalidName ? className + '__input--error' : ''}`}
                                                placeholder='Enter your full name'
                                                onChange={nameChangeHandler}
                                                disabled={cursor === 'wait'}
                                                value={name || ''}
                                            />
                                        </div>
                                        {invalidName && (
                                            <span className={className + '__fieldError'}>
                                                Full name is required
                                            </span>
                                        )}
                                    </div>

                                    <div className={className + '__inputGroup'}>
                                        <label className={className + '__label grey-text sm-text'}>Work Email</label>
                                        <div className={className + '__inputWrapper flex-row-center'}>
                                            <MdEmail className={className + '__inputIcon'} />
                                            <input
                                                type='email'
                                                className={`${className}__input ${invalidEmail || emailTaken ? className + '__input--error' : ''}`}
                                                placeholder='name@company.com'
                                                onChange={emailChangeHandler}
                                                disabled={cursor === 'wait'}
                                                value={email || ''}
                                            />
                                            {emailLoader && (
                                                <div className={className + '__inputLoader'}>
                                                    <CircularProgress size={20} thickness={4} />
                                                </div>
                                            )}
                                        </div>
                                        {invalidEmail && (
                                            <span className={className + '__fieldError'}>
                                                Please enter a valid email address
                                            </span>
                                        )}
                                        {emailTaken && (
                                            <span className={className + '__fieldError'}>
                                                This account already exists.{' '}
                                                <button className={className + '__linkBtn'} onClick={backToLogin}>
                                                    Log in instead
                                                </button>
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        className={className + '__primaryBtn'}
                                        onClick={handleSignupClick}
                                        disabled={invalidEmail || emailTaken || !email || email?.length === 0 || signupLoader || emailLoader || invalidName || !name || name?.length === 0}
                                    >
                                        {signupLoader ? (
                                            <>
                                                <div className={className + '__spinner'}></div>
                                                Creating account...
                                            </>
                                        ) : 'Create Account'}
                                    </button>
                                </form>

                                {/* Footer Links */}
                                <div className={className + '__footer flex-column-center gap-1rem with-wfa'}>
                                    <div className={className + '__footerText flex-row-center-1rem-gap sm-text grey-text width-wfa'}>
                                        Already have an account?{' '}
                                        <button className={className + '__linkBtn sm-text hovered'} onClick={backToLogin}>
                                            Sign in
                                        </button>
                                    </div>
                                    <div className={className + '__terms flex-row-center-0p5rem-gap grey-text sm-text width-wfa'}>
                                        By creating an account, you agree to our{' '}
                                        <button className={className + '__linkBtn sm-text hovered'} onClick={openTermsPage}>
                                            Terms and Policies
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Error View */}
                        {showErrorView && (
                            <div className={className + '__errorView'}>
                                <div className={className + '__errorCard'}>
                                    <div className={className + '__errorHeader'}>
                                        <MdWarning className={className + '__errorIcon'} />
                                        <h3 className={className + '__errorTitle'}>
                                            We couldn't complete your sign up
                                        </h3>
                                    </div>

                                    <div className={className + '__errorMessage'}>
                                        {(typeof errorCreatingUser === 'string' && errorCreatingUser.trim()) || 'Something went wrong. Please try again.'}
                                    </div>

                                    {/* Conflict Type Hints */}
                                    {conflictType === 'EXISTS_MANUAL_WHEN_SOCIAL' && (
                                        <div className={className + '__errorHint'}>
                                            Use your password to log in or reset it on the Log In page.
                                        </div>
                                    )}
                                    {(conflictType === 'EXISTS_SOCIAL_WHEN_MANUAL' || conflictType === 'EXISTS_SAME_SOCIAL_PLATFORM') && (
                                        <div className={className + '__errorHint'}>
                                            Continue with social login{existingPlatform ? ` via ${existingPlatform}` : ''}.
                                        </div>
                                    )}
                                    {conflictType === 'EXISTS_OTHER_SOCIAL_PLATFORM' && (
                                        <div className={className + '__errorHint'}>
                                            Account exists with a different social platform{existingPlatform ? ` (${existingPlatform})` : ''}. Use that platform on the Log In page.
                                        </div>
                                    )}

                                    <div className={className + '__errorActions'}>
                                        <button className={className + '__primaryBtn'} onClick={tryAgain}>
                                            Try Again
                                        </button>
                                        <button className={className + '__secondaryBtn'} onClick={backToLogin}>
                                            Go to Log In
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Loading Overlay */}
            {signupLoader && (
                <div className={className + '__loadingOverlay'}>
                    <div className={className + '__loadingContent'}>
                        <div className={className + '__loadingSpinner'}></div>
                        <p className={className + '__loadingText'}>Creating your account...</p>
                    </div>
                </div>
            )}
        </div>
    );
}